// Who works a task or one of its steps (spec J3, plan §4 PUT .../assignees). The people come from
// GET /people: only active people whose role works tasks can be picked (the API checks the same).
// Someone already assigned stays in the list, so they can be taken off.
import { useState, type FormEvent } from 'react';
import { hasPermission, isRole } from '@cis/shared';
import type { Person, PersonItem, TaskDetail } from '@cis/api-client';
import type { TaskWrite } from '../../api/tasks.ts';
import { usePeople } from '../../api/planning.ts';
import { ErrorPanel } from '../../ui/error-boundary.tsx';
import { Modal } from '../../ui/modal.tsx';
import { Skeleton } from '../../ui/skeleton.tsx';
import { DialogActions, ProblemText } from './dialog-parts.tsx';
import { baseFor, type TaskWriter } from './use-task-writer.ts';

const worksTasks = (p: PersonItem): boolean =>
  p.active && isRole(p.role) && hasPermission(p.role, 'task.transition');

/** The people to offer: everyone who can be assigned, plus whoever is assigned now. */
export function assignablePeople(
  people: readonly PersonItem[],
  current: readonly Person[],
): Person[] {
  const offered = people.filter(worksTasks).map((p) => ({ id: p.id, displayName: p.displayName }));
  const extra = current.filter((c) => !offered.some((p) => p.id === c.id));
  return [...offered, ...extra].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function PeopleChecks(props: {
  readonly people: readonly Person[];
  readonly chosen: ReadonlySet<number>;
  readonly onToggle: (id: number) => void;
}): React.JSX.Element {
  return (
    <fieldset className="checks">
      <legend className="field__label">People</legend>
      {props.people.map((p) => (
        <label key={p.id} className="check">
          <input
            type="checkbox"
            checked={props.chosen.has(p.id)}
            onChange={() => props.onToggle(p.id)}
          />
          {p.displayName}
        </label>
      ))}
    </fieldset>
  );
}

export interface AssignDialogProps {
  /** "Assign people to T-OPS-026" or "Assign step 2". */
  readonly title: string;
  readonly detail: TaskDetail;
  /** Who is assigned on a page: the task's people, or one step's. */
  readonly assigned: (d: TaskDetail) => readonly Person[];
  readonly writer: TaskWriter;
  readonly toWrite: (userIds: readonly number[]) => TaskWrite;
  readonly onClose: () => void;
}

export function AssignDialog(props: AssignDialogProps): React.JSX.Element {
  const { writer, assigned } = props;
  const people = usePeople();
  const [opened] = useState(props.detail);
  const current = assigned(opened);
  const [chosen, setChosen] = useState<ReadonlySet<number>>(
    () => new Set(current.map((p) => p.id)),
  );
  const [problem, setProblem] = useState<string | null>(null);
  const toggle = (id: number): void => {
    const next = new Set(chosen);
    if (!next.delete(id)) next.add(id);
    setChosen(next);
  };
  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const ids = (d: TaskDetail): number[] => assigned(d).map((p) => p.id);
    const write = props.toWrite([...chosen].sort((a, b) => a - b));
    const outcome = await writer.send(write, baseFor(opened, props.detail, ids));
    if (outcome === 'ok' || outcome === 'conflict') props.onClose();
    if (outcome === 'failed') setProblem(writer.lastError());
  };
  return (
    <Modal title={props.title} onClose={props.onClose} busy={writer.pending}>
      {people.isPending && <Skeleton label="Loading the people…" />}
      {people.isError && (
        <ErrorPanel
          title="The people could not be loaded"
          error={people.error}
          onRetry={() => void people.refetch()}
        />
      )}
      <form className="field" onSubmit={(e) => void onSubmit(e)}>
        {people.data !== undefined && (
          <PeopleChecks
            people={assignablePeople(people.data.items, current)}
            chosen={chosen}
            onToggle={toggle}
          />
        )}
        <ProblemText problem={problem} />
        <DialogActions
          writer={writer}
          onCancel={props.onClose}
          submitLabel="Save assignees"
          submitDisabled={people.data === undefined}
        />
      </form>
    </Modal>
  );
}
